/**
 * Таблица операций варианта техкарты (макет v2-05, блок «Операции варианта»).
 *
 * Строки сгруппированы блоками, вспомогательные работы («подвоз семян»,
 * «загрузка сеялок») идут свитой под своей основной операцией — так техкарту
 * читают на бумаге, и так её узнаёт агроном без обучения.
 *
 * Колонки «Сцепка» в этой версии нет намеренно: API отдаёт только
 * `machinery_unit_id`, а справочника парка техники (экран «Техника»,
 * v2-12) в системе ещё нет — подписывать строку идентификатором нельзя, а
 * ставить прочерк там, где сцепка на самом деле задана, значит врать.
 * Количество сцепок показываем: оно осмысленно и уходит в проверку
 * выполнимости.
 */
import { HardHat, Pencil, Plus, Trash2, Wheat } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  EXECUTION_TITLES,
  blockTitle,
  blocksOfCard,
  formatFuel,
  formatMaterial,
  formatMoney,
  formatMultiplicity,
  formatNumber,
  formatPerHa,
  formatVolume,
  formatWindow,
  formatWindowShort,
  operationsOfBlock,
  type BlockKind,
  type CardCost,
  type OperationCost,
  type TechCardOperation,
  type TechCardStage,
} from '@/lib/planning/techCards';

export interface OperationRowActions {
  onAddOperation: (kind: BlockKind) => void;
  onEditOperation: (op: TechCardOperation) => void;
  onEditMaterials: (op: TechCardOperation) => void;
  /** ФОТ по ролям + прямые суммы (ADR-071 D11, D5). */
  onEditLabor: (op: TechCardOperation) => void;
  onDeleteOperation: (op: TechCardOperation) => void;
}

/** Строки этапа: основная операция, под ней её свита (как в блоках). */
export function operationsOfStage(
  operations: TechCardOperation[],
  stageId: string | null,
): TechCardOperation[] {
  // Свита живёт ПОД родителем независимо от собственного stage_id: разорвать
  // «сев» и «подвоз семян» по разным секциям значило бы спрятать связь.
  const byParent = new Map<string, TechCardOperation[]>();
  for (const op of operations) {
    if (op.parent_operation_id) {
      const list = byParent.get(op.parent_operation_id) ?? [];
      list.push(op);
      byParent.set(op.parent_operation_id, list);
    }
  }
  const parents = operations.filter(
    (op) => !op.parent_operation_id && (op.stage_id ?? null) === stageId,
  );
  const out: TechCardOperation[] = [];
  for (const parent of parents) {
    out.push(parent, ...(byParent.get(parent.id) ?? []));
  }
  // Осиротевшая свита (родитель удалён или в другом источнике) без этапа.
  if (stageId === null) {
    for (const op of operations) {
      if (op.parent_operation_id && !operations.some((p) => p.id === op.parent_operation_id) && !out.includes(op)) {
        out.push(op);
      }
    }
  }
  return out;
}

export function OperationsTable({
  operations,
  cost,
  stages = [],
  onReplaceBlock,
  canReplace,
  activeBlock,
  actions,
}: {
  operations: TechCardOperation[];
  cost: CardCost | null;
  /** Этапы карты (ADR-071 D3). Есть — группируем по ним; нет — по блокам. */
  stages?: TechCardStage[];
  onReplaceBlock: (kind: BlockKind) => void;
  /** Право правки: и замена блока, и правка строк живут под `tech_cards:U`. */
  canReplace: boolean;
  activeBlock: BlockKind | null;
  actions: OperationRowActions;
}) {
  const perOp = new Map((cost?.operations ?? []).map((o) => [o.operation_id, o]));
  // Деньги строки в абсолюте (ADR-071 D6-b) осмысленны только на площади
  // назначения; в удельном режиме (1 га) они дублируют ₽/га и колонку прячем.
  const showAbs = cost?.basis_area === 'assigned';
  const blocks = blocksOfCard(operations);
  const orderedStages = [...stages].sort((a, b) => a.seq - b.seq || a.name.localeCompare(b.name, 'ru'));
  const byStage = orderedStages
    .map((stage) => ({ stage, rows: operationsOfStage(operations, stage.id) }))
    .filter((group) => group.rows.length > 0);
  const withoutStage = operationsOfStage(operations, null);
  const groupByStages = orderedStages.length > 0;

  if (operations.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          В этом варианте пока нет ни одной операции. Наберите их вручную или склонируйте
          готовый вариант — тогда останется поменять только блоки, которые отличаются.
        </p>
        {canReplace && (
          <Button size="sm" onClick={() => actions.onAddOperation('tillage')}>
            <Plus className="mr-2 h-4 w-4" />
            Добавить первую операцию
          </Button>
        )}
      </div>
    );
  }

  return (
    <div>
      {/*
        Таблица тянется на всю ширину контейнера: на 1920 это ~1566px, и все
        12 колонок видны без горизонтального скролла (замер в браузере).
        `min-w` оставлен как нижняя граница для
        ноутбуков и планшетов: там колонки не сжимаются в кашу, а честно
        уезжают в горизонтальный скролл — но кнопки действий из-под скролла
        вынесены в заголовок секции.
      */}
      <Table className="w-full min-w-[64rem]">
        <TableHeader>
          <TableRow className="text-xs">
            <TableHead>Этап / работа</TableHead>
            <TableHead>Агроокно</TableHead>
            <TableHead className="text-right">Раб. дней</TableHead>
            <TableHead className="text-right">Кратность</TableHead>
            <TableHead className="text-right">Объём</TableHead>
            <TableHead className="text-right">Кол-во сцепок</TableHead>
            <TableHead className="text-right">Норма выработки</TableHead>
            <TableHead className="text-right">Оплата труда</TableHead>
            <TableHead>Топливо</TableHead>
            <TableHead>Материалы (норма × плановая цена)</TableHead>
            <TableHead className="text-right">Итого</TableHead>
            {showAbs && <TableHead className="text-right">Деньги строки</TableHead>}
            {canReplace && <TableHead className="text-right">Правка</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupByStages ? (
            <>
              {byStage.map(({ stage, rows }) => (
                <StageRows
                  key={stage.id}
                  title={stage.name}
                  operations={rows}
                  perOp={perOp}
                  showAbs={showAbs}
                  canWrite={canReplace}
                  actions={actions}
                />
              ))}
              {withoutStage.length > 0 && (
                <StageRows
                  title="Без этапа"
                  muted
                  operations={withoutStage}
                  perOp={perOp}
                  showAbs={showAbs}
                  canWrite={canReplace}
                  actions={actions}
                />
              )}
            </>
          ) : (
            blocks.map((kind) => (
              <BlockRows
                key={kind}
                kind={kind}
                operations={operationsOfBlock(operations, kind)}
                perOp={perOp}
                showAbs={showAbs}
                onReplaceBlock={onReplaceBlock}
                canReplace={canReplace}
                isActive={activeBlock === kind}
                actions={actions}
              />
            ))
          )}
        </TableBody>
      </Table>
      <p className="mt-3 text-xs text-muted-foreground">
        Агроокно принадлежит ЭТОЙ техкарте, а не справочнику операций: два варианта одной
        культуры могут сеять в разные сроки. Число рабочих дней в окне и количество сцепок
        уходят в проверку выполнимости.
      </p>
    </div>
  );
}

function BlockRows({
  kind,
  operations,
  perOp,
  showAbs,
  onReplaceBlock,
  canReplace,
  isActive,
  actions,
}: {
  kind: BlockKind;
  operations: TechCardOperation[];
  perOp: Map<string, OperationCost>;
  showAbs: boolean;
  actions: OperationRowActions;
  onReplaceBlock: (kind: BlockKind) => void;
  canReplace: boolean;
  isActive: boolean;
}) {
  return (
    <>
      <TableRow className="bg-muted hover:bg-muted">
        <TableCell colSpan={11 + (showAbs ? 1 : 0) + (canReplace ? 1 : 0)} className="py-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-foreground">{blockTitle(kind)}</span>
            {isActive && (
              <span className="text-xs text-muted-foreground">
                подбирается замена — предпросмотр выше
              </span>
            )}
            {canReplace && (
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => actions.onAddOperation(kind)}
                  className="rounded-full border border-border bg-background px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  Добавить операцию
                </button>
                <button
                  type="button"
                  onClick={() => onReplaceBlock(kind)}
                  className="rounded-full border border-border bg-background px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  Заменить блок
                </button>
              </div>
            )}
          </div>
        </TableCell>
      </TableRow>
      {operations.map((op) => (
        <OperationRowCells key={op.id} op={op} perOp={perOp} showAbs={showAbs} canWrite={canReplace} actions={actions} />
      ))}
    </>
  );
}

/** Секция ЭТАПА (ADR-071 D3): серая строка с именем этапа + его работы. */
function StageRows({
  title: sectionTitle,
  muted = false,
  operations,
  perOp,
  showAbs,
  canWrite,
  actions,
}: {
  title: string;
  muted?: boolean;
  operations: TechCardOperation[];
  perOp: Map<string, OperationCost>;
  showAbs: boolean;
  canWrite: boolean;
  actions: OperationRowActions;
}) {
  return (
    <>
      <TableRow className="bg-muted hover:bg-muted">
        <TableCell colSpan={11 + (showAbs ? 1 : 0) + (canWrite ? 1 : 0)} className="py-2">
          <span className={muted ? 'font-semibold text-muted-foreground' : 'font-semibold text-foreground'}>
            {sectionTitle}
          </span>
          <span className="ml-2 text-xs text-muted-foreground">
            {operations.length === 1 ? '1 операция' : `${operations.length} операций`}
          </span>
        </TableCell>
      </TableRow>
      {operations.map((op) => (
        <OperationRowCells key={op.id} op={op} perOp={perOp} showAbs={showAbs} canWrite={canWrite} actions={actions} />
      ))}
    </>
  );
}

/** Одна строка операции — общая для группировок по блокам и по этапам. */
function OperationRowCells({
  op,
  perOp,
  showAbs,
  canWrite,
  actions,
}: {
  op: TechCardOperation;
  perOp: Map<string, OperationCost>;
  showAbs: boolean;
  canWrite: boolean;
  actions: OperationRowActions;
}) {
  const money = perOp.get(op.id);
  const aux = !!op.parent_operation_id;
  const share = op.area_share_pct ?? 100;
  return (
    <TableRow className={aux ? 'text-muted-foreground' : 'text-foreground'}>
      <TableCell className="py-2">
        <div className={aux ? 'flex items-center gap-2 pl-4' : 'flex items-center gap-2'}>
          <span>
            {aux ? '↳ ' : ''}
            {title(op)}
          </span>
          {share < 100 && (
            <span className="text-xs text-muted-foreground" title="Доля обрабатываемой площади">
              {formatNumber(share)} % площади
            </span>
          )}
          {!aux && (
            <Badge variant={op.execution_kind === 'own' ? 'outline' : 'accent-brand'}>
              {EXECUTION_TITLES[op.execution_kind]}
            </Badge>
          )}
          {op.is_wip && (
            <Badge
              variant="outline"
              title={
                op.wip_override != null
                  ? 'НЗП — отнесение задано вручную (правится в форме работы)'
                  : 'НЗП — осенняя работа под этот урожай, выполняется в прошлом сезоне'
              }
            >
              НЗП{op.wip_override != null ? ' · вручную' : ''}
            </Badge>
          )}
          {!op.is_wip && op.wip_override != null && (
            <Badge variant="outline" title="Отнесение к НЗП снято вручную (правится в форме работы)">
              не НЗП · вручную
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="py-2 whitespace-nowrap" title={formatWindow(op)}>
        {formatWindowShort(op)}
      </TableCell>
      <TableCell className="py-2 text-right">{formatNumber(op.window_work_days)}</TableCell>
      <TableCell className="py-2 text-right whitespace-nowrap">
        {formatMultiplicity(op.multiplicity)}
      </TableCell>
      <TableCell className="py-2 text-right whitespace-nowrap">{formatVolume(op)}</TableCell>
      <TableCell className="py-2 text-right">{formatNumber(op.machinery_count)}</TableCell>
      <TableCell className="py-2 text-right">{formatNumber(op.throughput_norm)}</TableCell>
      <TableCell className="py-2 text-right whitespace-nowrap">
        {formatPerHa(money?.labor ?? null)}
      </TableCell>
      <TableCell className="py-2 whitespace-nowrap">{formatFuel(op)}</TableCell>
      <TableCell className="py-2">
        {op.materials && op.materials.length > 0
          ? op.materials.map((m) => formatMaterial(m)).join(' · ')
          : '—'}
      </TableCell>
      <TableCell className="py-2 text-right font-medium whitespace-nowrap">
        {formatPerHa(money?.total ?? null)}
      </TableCell>
      {showAbs && (
        <TableCell
          className="py-2 text-right font-medium whitespace-nowrap"
          title="Деньги строки на площадь назначения (как в сводной техкарте)"
        >
          {formatMoney(money?.total_abs ?? null)}
        </TableCell>
      )}
      {canWrite && (
        <TableCell className="py-2 text-right whitespace-nowrap">
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Изменить операцию ${title(op)}`}
            onClick={() => actions.onEditOperation(op)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Материалы операции ${title(op)}`}
            onClick={() => actions.onEditMaterials(op)}
          >
            <Wheat className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Оплата труда операции ${title(op)}`}
            title="Оплата труда: роли, премия, вредность, ЕСН + прямые суммы"
            onClick={() => actions.onEditLabor(op)}
          >
            <HardHat className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Удалить операцию ${title(op)}`}
            onClick={() => actions.onDeleteOperation(op)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </TableCell>
      )}
    </TableRow>
  );
}

/** Подпись операции в строке и в подписях кнопок — одна и та же. */
function title(op: TechCardOperation): string {
  return op.name ?? op.op_type_name ?? 'операция без названия';
}
